import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import Navbar from "../../components/Navbar";
import Footer from "../../components/Footer";
import {
  ArrowLeft,
  Share2,
  Linkedin,
  CheckCircle2,
  Clock,
  Phone,
  MessageCircle,
  Building2,
  Megaphone,
  Mail,
  Target,
  MapPin,
  Users,
  BarChart3,
  Gift,
  DollarSign,
  Eye,
  Calendar,
  Instagram,
  ExternalLink,
  Heart,
  Bookmark,
  Flag,
  Loader2,
} from "lucide-react";
import campaignAdvertisingService from "../../company/services/campaignAdvertisingService";
import campaignApplicationService from "../../company/services/campaignApplicationService";
import influencerActivityService from "../../influencer/services/influencerActivityService";
import { followerRangeMap } from "./SampleCampaignData";
import { FaArrowRight, FaCheckCircle } from "react-icons/fa";
import { toast } from "react-toastify";

// Helper function to format time ago
const formatTimeAgo = (date) => {
  if (!date) return "Recently";
  const now = new Date();
  const past = new Date(date);
  const diffInSeconds = Math.floor((now - past) / 1000);

  if (diffInSeconds < 60) return "just now";
  if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
  if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
  if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)}d ago`;
  return past.toLocaleDateString();
};

// Helper function to format date
const formatDate = (date) => {
  if (!date) return "N/A";
  return new Date(date).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  });
};



const formatFollowerRangeDisplay = (ranges = []) => {
  if (!ranges.length) return "Not specified";
  return ranges
    .map((range) => {
      const detail = followerRangeMap[range];
      if (detail) {
        return `${range} (Instagram: ${detail.instagram} | YouTube: ${detail.youtube})`;
      }
      return range;
    })
    .join(", ");
};

const CampaignDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [campaign, setCampaign] = useState(null);
  const [isBookmarked, setIsBookmarked] = useState(false);
  const [isLiked, setIsLiked] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hasApplied, setHasApplied] = useState(false);
  const [checkingApplication, setCheckingApplication] = useState(false);
  const [similarCampaigns, setSimilarCampaigns] = useState([]);
  const [totalViews, setTotalViews] = useState(0);
  const [totalLikes, setTotalLikes] = useState(0);
  const [updatingActivity, setUpdatingActivity] = useState(false);

  // Get user state from Redux
  const user = useSelector((state) => state.user.data);
  const company = useSelector((state) => state.company.company);
  const token = localStorage.getItem('token');
  const userType = localStorage.getItem('userType');

  // Determine user type and login status
  const isInfluencer = userType === 'influancer' && !!(token && user);
  const isCompany = userType === 'company' && !!(token && company);
  const isLoggedIn = isInfluencer || isCompany;

  // Track view when campaign is loaded
  const trackView = async (campaignAdvertisingId) => {
    try {
      const response = await influencerActivityService.trackView(campaignAdvertisingId);
      if (response.success && response.totalViews !== undefined) {
        setTotalViews(response.totalViews);
        // Update campaign activity
        setCampaign(prev => prev ? {
          ...prev,
          activity: {
            ...prev.activity,
            totalViews: response.totalViews
          }
        } : null);
      }
    } catch (err) {
      console.error('Error tracking view:', err);
    }
  };

  // Fetch user activity (likes, bookmarks)
  const fetchUserActivity = async (campaignAdvertisingId) => {
    try {
      const response = await influencerActivityService.getUserActivity(campaignAdvertisingId);
      if (response.success && response.data) {
        setIsLiked(response.data.liked || false);
        setIsBookmarked(response.data.bookmarked || false);
        if (response.data.totalViews !== undefined) {
          setTotalViews(response.data.totalViews);
        }
        if (response.data.totalLikes !== undefined) {
          setTotalLikes(response.data.totalLikes);
        }

        // Update campaign activity
        setCampaign(prev => prev ? {
          ...prev,
          activity: {
            ...prev.activity,
            totalViews: response.data.totalViews || prev.activity.totalViews || 0
          }
        } : null);
      }
    } catch (err) {
      console.error('Error fetching user activity:', err);
    }
  };

  // Fetch similar campaigns by hireType
  const fetchSimilarCampaigns = async (hireType, excludeId) => {
    try {
      const response = await campaignAdvertisingService.getPublishedCampaignAdvertisings({
        hireType,
        limit: 3
      });

      if (response.success && response.data) {
        // Filter out current campaign and transform data
        const similar = response.data
        .filter(item => item.id !== excludeId)
        .slice(0, 2)
        .map(item => ({
          id: item.id,
          name: item.brandManager?.name || item.brand?.name || "Brand",
          timeAgo: "Recently",
          profileImage: `https://picsum.photos/100?random=${item.id.substring(0, 8)}`,
          description: item.requirement || "",
          attributes: {
            platform: item.platform?.join(", ") || "Not specified",
          }
        }));
      
        setSimilarCampaigns(similar);
      }
    } catch (err) {
      console.error('Error fetching similar campaigns:', err);
    }
  };

  // Fetch campaign advertising data
  useEffect(() => {
    const fetchCampaign = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch all published campaigns and find the one with matching ID
        const response = await campaignAdvertisingService.getPublishedCampaignAdvertisings();

        if (response.success && response.data) {
          const foundCampaign = response.data.find(item => item.id === id);

          if (foundCampaign) {
            const followerRangeDisplay = formatFollowerRangeDisplay(foundCampaign.followerRange || []);

            const transformedCampaign = {
              id: foundCampaign.id,

              name: foundCampaign.brandManager?.name || "Brand Manager",
              profession: foundCampaign.brandManager?.position || "Brand Manager",

              timeAgo: formatTimeAgo(foundCampaign.createdAt) || "Recently",

              profileImage: `https://picsum.photos/100?random=${foundCampaign.id.substring(0, 8)}`,

              linkedin: foundCampaign.brandManager?.linkedin || "#",
              verified: true,

              phone: foundCampaign.brandManager?.phone || "",
              whatsapp: foundCampaign.brandManager?.phone || "",
              email: foundCampaign.brandManager?.email || "",

              lookingFor:
                foundCampaign.hireType === "agency"
                  ? "Influencer Marketing Agencies"
                  : "Content Creators",

              description: foundCampaign.requirement || "",

              attributes: {
                brandName: foundCampaign.brand?.name || foundCampaign.brandName || "Not disclosed",
                campaignType: foundCampaign.campaign?.name || "Campaign",
                platform: foundCampaign.platform?.join(", ") || "Not specified",
                creatorsCategory: foundCampaign.category || "Not specified",
                followerRange: followerRangeDisplay,
                deliverables: foundCampaign.deliverables || "Not specified",
                agencyName: foundCampaign.agencyName || "Not specified",
                budgetRange: foundCampaign.budget || "Not disclosed",
              },

              keyHighlights: {
                platform: foundCampaign.platform?.join(", ") || "Not specified",
              },

              activity: {
                totalViews: 0,
                postedOn: formatDate(foundCampaign.campaign?.startDate),
                expiresOn: formatDate(foundCampaign.campaign?.endDate),
              },

              hireType: foundCampaign.hireType,
              status: foundCampaign.status,
              category: foundCampaign.category || [],
              location: foundCampaign.location || [],
              language: foundCampaign.language || [],
              gender: foundCampaign.gender || "",
            };

            setCampaign(transformedCampaign);

            // Track view (only for influencers)
            if (isInfluencer) {
              trackView(id);
              // Fetch user activity (likes, bookmarks) only for influencers
              fetchUserActivity(id);
            }

            // Fetch similar campaigns
            fetchSimilarCampaigns(foundCampaign.hireType, foundCampaign.id);
          } else {
            setError("Campaign not found");
          }
        } else {
          setError("Failed to load campaign");
        }
      } catch (err) {
        console.error("Error fetching campaign:", err);
        setError(err.message || "Failed to load campaign");
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchCampaign();
    }
  }, [id, isInfluencer]);

  // Handle like toggle
  const handleLike = async () => {
    if (!isInfluencer) {
      toast.info('Please login as an influencer to like campaigns');
      return;
    }

    try {
      setUpdatingActivity(true);
      const response = await influencerActivityService.toggleLike(id);
      if (response.success) {
        setIsLiked(response.liked);
        setTotalLikes(response.totalLikes || 0);
        // toast.success(response.liked ? 'Campaign liked' : 'Like removed');
      }
    } catch (err) {
      console.error('Error toggling like:', err);
      toast.error(err.message || 'Failed to update like');
    } finally {
      setUpdatingActivity(false);
    }
  };

  // Handle bookmark toggle
  const handleBookmark = async () => {
    if (!isInfluencer) {
      toast.info('Please login as an influencer to bookmark campaigns');
      return;
    }

    try {
      setUpdatingActivity(true);
      const response = await influencerActivityService.toggleBookmark(id);
      if (response.success) {
        setIsBookmarked(response.bookmarked);
        // toast.success(response.bookmarked ? 'Campaign bookmarked' : 'Bookmark removed');
      }
    } catch (err) {
      console.error('Error toggling bookmark:', err);
      toast.error(err.message || 'Failed to update bookmark');
    } finally {
      setUpdatingActivity(false);
    }
  };

  // Check if influencer has applied to this campaign advertising
  // Only check for influencers, not companies
  useEffect(() => {
    const checkApplicationStatus = async () => {
      // Only check application status for influencers
      if (!id || !isInfluencer) {
        setHasApplied(false);
        setCheckingApplication(false);
        return;
      }

      try {
        setCheckingApplication(true);
        const response = await campaignApplicationService.checkUserApplication(id);
        if (response.success) {
          setHasApplied(response.hasApplied || false);
        } else {
          setHasApplied(false);
        }
      } catch (err) {
        console.error('Error checking application status:', err);
        // Don't show error to user, just assume not applied
        setHasApplied(false);
      } finally {
        setCheckingApplication(false);
      }
    };

    checkApplicationStatus();
  }, [id, isInfluencer]);

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: `${campaign?.name} - Campaign`,
        text: campaign?.description,
        url: window.location.href,
      });
    } else {
      // Fallback: Copy to clipboard
      navigator.clipboard.writeText(window.location.href);
      alert("Link copied to clipboard!");
    }
  };

  const handleCall = () => {
    if (campaign?.phone) {
      window.location.href = `tel:${campaign.phone.replace(/\s/g, "")}`;
    }
  };

  const handleWhatsApp = () => {
    if (campaign?.whatsapp) {
      const message = encodeURIComponent(
        `Hi, I'm interested in your campaign: ${campaign.description}`
      );
      window.open(
        `https://wa.me/${campaign.whatsapp.replace(/\s/g, "")}?text=${message}`,
        "_blank"
      );
    }
  };
  const handleEmail = () => {
    if (campaign?.email) {
      window.location.href = `mailto:${campaign.email}`;
    }
  };

  const handleApplyNow = () => {
    // Companies cannot apply to campaigns
    if (isCompany) {
      toast.info('Companies cannot apply to campaigns');
      return;
    }

    const redirectTo = `/campaign/${id}/submit`;

    if (isInfluencer) {
      // Influencer is logged in, redirect directly to submission form
      navigate(redirectTo);
    } else {
      // User is not logged in, redirect to login with redirectTo parameter
      navigate(`/login?redirectTo=${encodeURIComponent(redirectTo)}`);
    }
  };

  if (loading) {
    return (
      <div className="bg-gray-50 min-h-screen flex items-center justify-center">
        <Navbar />
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading campaign details...</p>
        </div>
      </div>
    );
  }

  if (error || !campaign) {
    return (
      <div className="bg-gray-50 min-h-screen flex items-center justify-center">
        <Navbar />
        <div className="text-center">
          <p className="text-red-600 mb-4">{error || "Campaign not found"}</p>
          <button
            onClick={() => navigate("/campaign")}
            className="bg-orange-500 hover:bg-orange-600 text-white px-6 py-2 rounded-lg"
          >
            Back to Campaigns
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 min-h-screen flex flex-col">
      <Navbar />
      <div className="flex-grow pt-20 pb-12">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-4xl">
          {/* Header Section */}
          <div className="flex items-center justify-between mb-6">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center gap-2 text-gray-700 hover:text-gray-900 transition-colors group"
            >
              <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
              <span className="hidden sm:inline font-medium">Back</span>
            </button>
            <button
              onClick={handleShare}
              className="bg-orange-600 hover:bg-orange-700 text-white px-4 sm:px-6 py-2.5 rounded-lg font-medium transition-colors flex items-center gap-2 shadow-sm hover:shadow-md"
            >
              <Share2 className="w-4 h-4 sm:w-5 sm:h-5" />
              <span className="hidden sm:inline">Share</span>
            </button>
          </div>

          {/* Main Content Card */}
          <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
            {/* Profile and Contact Section */}
            <div className="p-4 sm:p-6 lg:p-8 border-b border-gray-100">
              <div className="flex flex-col sm:flex-row gap-4 sm:gap-6">
                {/* Profile Image */}
                <div className="flex-shrink-0">
                  <div className="w-16 h-16 sm:w-20 sm:h-20 lg:w-24 lg:h-24 rounded-full bg-gray-200 overflow-hidden ring-2 ring-gray-100">
                    <img
                      src={campaign.profileImage}
                      alt={campaign.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>

                {/* Profile Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2 mb-2">
                    <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900">
                      {campaign.name}
                    </h1>
                    {campaign.verified && (
                      <CheckCircle2 className="w-5 h-5 sm:w-6 sm:h-6 text-green-500 flex-shrink-0" />
                    )}
                    <a
                      href={campaign.linkedin}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:text-blue-700 transition-colors flex-shrink-0"
                    >
                      <Linkedin className="w-5 h-5 sm:w-6 sm:h-6" />
                    </a>
                  </div>
                  <p className="text-sm sm:text-base text-gray-600 mb-2">
                    {campaign.profession}
                  </p>
                  <div className="flex items-center gap-1 text-gray-500 text-xs sm:text-sm mb-4">
                    <Clock className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                    <span>{campaign.timeAgo}</span>
                  </div>

                  {/* Call and WhatsApp Buttons */}
                  <div className="flex flex-col sm:flex-row gap-3">
                    <button
                      onClick={handleCall}
                      className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2.5 rounded-lg font-medium transition-colors flex items-center justify-center gap-2 group"
                    >
                      <Phone className="w-4 h-4" />
                      <span>Call</span>
                      <ExternalLink className="w-3.5 h-3.5 opacity-50 group-hover:opacity-100 transition-opacity" />
                    </button>
                    <button
                      onClick={handleWhatsApp}
                      className="flex-1 bg-green-600 hover:bg-green-700 text-white px-4 py-2.5 rounded-lg font-medium transition-colors flex items-center justify-center gap-2 shadow-sm hover:shadow-md"
                    >
                      <MessageCircle className="w-4 h-4" />
                      <span>WhatsApp</span>
                    </button>
                    <button
                      onClick={handleEmail}
                      className="flex-1 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2.5 rounded-lg font-medium transition-colors flex items-center justify-center gap-2 shadow-sm hover:shadow-md"
                    >
                      <Mail className="w-4 h-4" />
                      <span>Email</span>
                    </button>
                    {/* Show Apply Now button only for influencers or non-logged-in users */}
                    {!isCompany && (
                      <>
                        {checkingApplication ? (
                          <button
                            disabled
                            className="flex-1 bg-orange-600 text-white px-4 py-2.5 rounded-lg font-medium flex items-center justify-center gap-2 shadow-sm cursor-not-allowed opacity-75"
                          >
                            <Loader2 className="w-4 h-4 animate-spin" />
                            <span>Checking...</span>
                          </button>
                        ) : hasApplied ? (
                          <button
                            disabled
                            className="flex-1 bg-green-600 text-white px-4 py-2.5 rounded-lg font-medium flex items-center justify-center gap-2 shadow-sm cursor-not-allowed opacity-90"
                          >
                            <FaCheckCircle className="w-4 h-4" />
                            <span>Applied</span>
                          </button>
                        ) : (
                          <button
                            onClick={handleApplyNow}
                            className="flex-1 bg-orange-600 hover:bg-orange-700 text-white px-4 py-2.5 rounded-lg font-medium transition-colors flex items-center justify-center gap-2 shadow-sm hover:shadow-md"
                          >
                            <FaArrowRight className="w-4 h-4" />
                            <span>Apply Now</span>
                          </button>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Looking For Section */}
            <div className="p-4 sm:p-6 lg:p-8 border-b border-gray-100">
              <h2 className="text-lg sm:text-xl font-bold text-gray-900 mb-3">
                Looking for
              </h2>
              <div className="flex items-center gap-3 text-gray-700">
                <Building2 className="w-5 h-5 text-purple-600 flex-shrink-0" />
                <span className="text-base sm:text-lg">{campaign.lookingFor}</span>
              </div>
            </div>

            {/* Campaign Description */}
            <div className="p-4 sm:p-6 lg:p-8 border-b border-gray-100">
              <p className="text-gray-700 text-base sm:text-lg leading-relaxed">
                {campaign.description}
              </p>
            </div>

            {/* Detailed Campaign Specifications */}
            <div className="p-4 sm:p-6 lg:p-8 border-b border-gray-100">
              <div className="space-y-4">
                {/* Brand Name */}
                <div className="flex items-start gap-3 sm:gap-4">
                  <Megaphone className="w-5 h-5 sm:w-6 sm:h-6 text-yellow-500 flex-shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <span className="text-sm sm:text-base text-gray-600">Brand Name: </span>
                    <span className="text-sm sm:text-base font-medium text-gray-900">
                      {campaign.attributes.brandName}
                    </span>
                  </div>
                </div>

                {/* Campaign Type */}
                <div className="flex items-start gap-3 sm:gap-4">
                  <Target className="w-5 h-5 sm:w-6 sm:h-6 text-red-500 flex-shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <span className="text-sm sm:text-base text-gray-600">Campaign Type: </span>
                    <span className="text-sm sm:text-base font-medium text-gray-900">
                      {campaign.attributes.campaignType}
                    </span>
                  </div>
                </div>

                {/* Platform */}
                <div className="flex items-start gap-3 sm:gap-4">
                  <MapPin className="w-5 h-5 sm:w-6 sm:h-6 text-red-500 flex-shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <span className="text-sm sm:text-base text-gray-600">Platform: </span>
                    <span className="text-sm sm:text-base font-medium text-gray-900">
                      {campaign.attributes.platform}
                    </span>
                  </div>
                </div>

                {/* Creators Category */}
                <div className="flex items-start gap-3 sm:gap-4">
                  <Users className="w-5 h-5 sm:w-6 sm:h-6 text-blue-500 flex-shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <span className="text-sm sm:text-base text-gray-600">Creators Category: </span>
                    <span className="text-sm sm:text-base font-medium text-gray-900">
                      {campaign.attributes.creatorsCategory}
                    </span>
                  </div>
                </div>

                {/* Follower Range */}
                <div className="flex items-start gap-3 sm:gap-4">
                  <BarChart3 className="w-5 h-5 sm:w-6 sm:h-6 text-green-500 flex-shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <span className="text-sm sm:text-base text-gray-600">Follower Range: </span>
                    <span className="text-sm sm:text-base font-medium text-gray-900">
                      {campaign.attributes.followerRange}
                    </span>
                  </div>
                </div>

                {/* Deliverables */}
                <div className="flex items-start gap-3 sm:gap-4">
                  <Gift className="w-5 h-5 sm:w-6 sm:h-6 text-red-500 flex-shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <span className="text-sm sm:text-base text-gray-600">Deliverables: </span>
                    <span className="text-sm sm:text-base font-medium text-gray-900">
                      {campaign.attributes.deliverables}
                    </span>
                  </div>
                </div>

                  {/* AgencyName */}
                  <div className="flex items-start gap-3 sm:gap-4">
                  <Gift className="w-5 h-5 sm:w-6 sm:h-6 text-red-500 flex-shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <span className="text-sm sm:text-base text-gray-600">Agency Name: </span>
                    <span className="text-sm sm:text-base font-medium text-gray-900">
                      {campaign.attributes.agencyName}
                    </span>
                  </div>
                </div>

                {/* Budget Range */}
                <div className="flex items-start gap-3 sm:gap-4">
                  <DollarSign className="w-5 h-5 sm:w-6 sm:h-6 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <span className="text-sm sm:text-base text-gray-600">Budget Range: </span>
                    <span className="text-sm sm:text-base font-medium text-gray-900">
                      {campaign.attributes.budgetRange}
                    </span>
                  </div>
                </div>
              </div>

              {/* Visual Separator */}
              <div className="mt-6 flex gap-2">
                <div className="h-1 flex-1 bg-gradient-to-r from-green-400 to-green-600 rounded-full"></div>
                <div className="h-1 flex-1 bg-gradient-to-r from-blue-400 to-blue-600 rounded-full"></div>
              </div>
            </div>

            {/* Key Highlighted Details */}
            <div className="p-4 sm:p-6 lg:p-8 border-b border-gray-100">
              <h2 className="text-lg sm:text-xl font-bold text-gray-900 mb-4">
                Key Highlighted Details
              </h2>
              <div className="flex items-center gap-3 bg-orange-50 rounded-lg p-4">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-orange-200 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Instagram className="w-5 h-5 sm:w-6 sm:h-6 text-orange-600" />
                </div>
                <div className="flex-1">
                  <span className="text-sm sm:text-base text-gray-600">Platform</span>
                  <p className="text-base sm:text-lg font-semibold text-gray-900">
                    {campaign.keyHighlights.platform}
                  </p>
                </div>
              </div>
            </div>

            {/* Activity on this Requirement */}
            <div className="p-4 sm:p-6 lg:p-8">
              <h2 className="text-lg sm:text-xl font-bold text-gray-900 mb-4">
                Activity on this requirement
              </h2>
              <div className="space-y-3 sm:space-y-4">
                <div className="flex items-center gap-3 text-gray-700">
                  <Eye className="w-5 h-5 text-gray-500 flex-shrink-0" />
                  <span className="text-sm sm:text-base">Total views</span>
                  <span className="ml-auto text-sm sm:text-base font-semibold text-gray-900">
                    {totalViews || campaign.activity.totalViews || 0}
                  </span>
                </div>
                <div className="flex items-center gap-3 text-gray-700">
                  <Calendar className="w-5 h-5 text-gray-500 flex-shrink-0" />
                  <span className="text-sm sm:text-base">Post Start on</span>
                  <span className="ml-auto text-sm sm:text-base font-semibold text-gray-900">
                    {campaign.activity.postedOn}
                  </span>
                </div>
                <div className="flex items-center gap-3 text-gray-700">
                  <Clock className="w-5 h-5 text-gray-500 flex-shrink-0" />
                  <span className="text-sm sm:text-base">Post expires on</span>
                  <span className="ml-auto text-sm sm:text-base font-semibold text-gray-900">
                    {campaign.activity.expiresOn}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons - Additional Features */}
          <div className="mt-6 flex flex-wrap gap-3 justify-center sm:justify-start">
            <button
              onClick={handleLike}
              disabled={updatingActivity || !isInfluencer}
              className={`px-4 sm:px-6 py-2.5 rounded-lg font-medium transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed ${isLiked
                  ? "bg-red-50 text-red-600 hover:bg-red-100"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
            >
              <Heart
                className={`w-4 h-4 sm:w-5 sm:h-5 ${isLiked ? "fill-current" : ""}`}
              />
              <span className="hidden sm:inline">
                {isLiked ? `Liked${totalLikes > 0 ? ` (${totalLikes})` : ''}` : "Like"}
              </span>
            </button>
            <button
              onClick={handleBookmark}
              disabled={updatingActivity || !isInfluencer}
              className={`px-4 sm:px-6 py-2.5 rounded-lg font-medium transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed ${isBookmarked
                  ? "bg-orange-50 text-orange-600 hover:bg-orange-100"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
            >
              <Bookmark
                className={`w-4 h-4 sm:w-5 sm:h-5 ${isBookmarked ? "fill-current" : ""}`}
              />
              <span className="hidden sm:inline">
                {isBookmarked ? "Bookmarked" : "Bookmark"}
              </span>
            </button>
            {/* <button className="px-4 sm:px-6 py-2.5 rounded-lg font-medium transition-colors flex items-center gap-2 bg-gray-100 text-gray-700 hover:bg-gray-200">
              <Flag className="w-4 h-4 sm:w-5 sm:h-5" />
              <span className="hidden sm:inline">Report</span>
            </button> */}
          </div>

          {/* Similar Campaigns Section */}
          {similarCampaigns.length > 0 && (
            <div className="mt-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Similar Campaigns</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {similarCampaigns.map((relatedCampaign) => (
                  <div
                    key={relatedCampaign.id}
                    onClick={() => navigate(`/campaign/${relatedCampaign.id}`)}
                    className="bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow p-4 sm:p-6 cursor-pointer border border-gray-100 hover:border-orange-200"
                  >
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-12 h-12 rounded-full bg-gray-200 overflow-hidden flex-shrink-0">
                        <img
                          src={relatedCampaign.profileImage}
                          alt={relatedCampaign.name}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-gray-900 truncate">
                          {relatedCampaign.name}
                        </h3>
                        <p className="text-xs text-gray-500">{relatedCampaign.timeAgo}</p>
                      </div>
                    </div>
                    <p className="text-sm text-gray-700 line-clamp-2 mb-2">
                      {relatedCampaign.description}
                    </p>
                    <div className="flex items-center gap-2 text-xs text-gray-500">
                      <MapPin className="w-3.5 h-3.5" />
                      <span>{relatedCampaign.attributes.platform}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default CampaignDetails;

